import React, { useMemo } from 'react';
import { activeDashboardSelector } from '@src/store/tempDashboard/selectors';
import { useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { message } from 'antd';
import { isAppModule } from '@utils/constants';
import { getFilters, getFiltersForAppExperience } from '@src/api/filters';
import { addCompareFilterOnTrends } from '@store/trends/base/actions';
import { trackQueryError } from '@utils/sensor/apptracker';
import { compareOnTrendsFiltersSelector } from '@store/trends/base/selectors';
import { COMPARE_SERIES_LIMIT } from '@components/metrics-panel-hz/utils/compareSeriesOption';
import { getCompareLimitWarning } from '@store/trends/base/drawerSaga';
import isValidSavedFilter, { type Dimension } from '@src/utils/filter/isValidSavedFilter';
import { FIlTER_CONTAINS_INAPPLICALBE_DIMENSION_ERROR } from '@components/summary-panel/FilterDropdown/FilterStatus';
import { allDimensionsListSelector } from '@store/common/dimensionList/selectors';
import { StyledSelect } from './styled';

const RECENT_FILTER_COUNT = 10;

function SavedFilters() {
  const dispatch = useDispatch();
  const appliedCompareFilters = useSelector(compareOnTrendsFiltersSelector);
  const { dataScope } = useSelector(activeDashboardSelector);
  const allDimensions = useSelector(allDimensionsListSelector) as Dimension[];

  const { isFetching, data = [] } = useQuery(
    ['saved-filters'],
    () => {
      return isAppModule ? getFiltersForAppExperience() : getFilters();
    },
    {
      retry: 3,
      refetchOnWindowFocus: false,
      enabled: true,
      staleTime: 5 * 60 * 1000, // 5m
      placeholderData: [],
      onError: (err) => {
        trackQueryError({ type: 'load-saved-filters', err });
      },
    },
  );

  const options = useMemo(() => {
    // format filters
    const formattedOptions = data.map((rec) => ({
      value: rec.fid,
      label: rec.name,
      filter: { name: rec.name, rule: rec.rule, fid: rec.fid },
      title: rec.rule_text,
      category: rec.category,
    }));

    // recent filters
    const optionGroup = [
      {
        label: 'Recent',
        options: formattedOptions.slice(-RECENT_FILTER_COUNT).reverse(),
      },
    ];

    // rest of the filters grouped by category
    const rest = formattedOptions.slice(0, -RECENT_FILTER_COUNT);
    const groups = {};
    rest.forEach((opt) => {
      if (opt.category in groups) {
        groups[opt.category].push(opt);
      } else {
        groups[opt.category] = [opt];
      }
    });

    Object.keys(groups).forEach((groupKey) => {
      optionGroup.push({
        label: groupKey,
        options: groups[groupKey],
      });
    });

    return optionGroup;
  }, [data]);

  const handleChange = (_, { filter }) => {
    // validate filter limit
    if (appliedCompareFilters.length >= COMPARE_SERIES_LIMIT) {
      message.warning(getCompareLimitWarning(COMPARE_SERIES_LIMIT));
      return;
    }

    // saved filter including event dimension can't be selected under Session data scope
    if (isAppModule && dataScope) {
      const isValidFilter = isValidSavedFilter(filter, allDimensions);
      if (!isValidFilter) {
        message.warning(FIlTER_CONTAINS_INAPPLICALBE_DIMENSION_ERROR);
        return;
      }
    }
    dispatch(addCompareFilterOnTrends(filter));
  };

  return (
    <StyledSelect
      showSearch
      optionFilterProp="label"
      optionLabelProp="label"
      placeholder={'Add saved filters'}
      value={null}
      onChange={handleChange}
      options={options}
      loading={isFetching}
      dropdownStyle={{ minWidth: 500 }}
      placement="bottomRight"
    />
  );
}

export default SavedFilters;
